import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    type ButtonHTMLAttributes,
    type MouseEventHandler,
} from 'react'

import { Tooltip } from '@core/components/v2/Tooltip'

import ApplySkeleton from '../../ApplySkeleton'

import './Button.scss'

const baseClass = 'ApplyDialogV2-v2-Button'

function hasContent(value: React.ReactNode): boolean {
    return value != null && typeof value !== 'boolean' && !(typeof value === 'string' && value.trim() === '')
}

export type ButtonVariant = 'primary' | 'secondary' | 'header'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    [attribute: `data-${string}`]: string | number | boolean | undefined
    children?: React.ReactNode
    variant?: ButtonVariant
    isDisabled?: boolean
    isLoading?: boolean
    isSkeleton?: boolean
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
    tooltip?: string
    tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
    onClickInside?: MouseEventHandler<HTMLButtonElement>
    // Outside clicks originate from the document, rather than React's button event.
    onClickOutside?: (event: MouseEvent) => void
    'ra-data-job-post-id'?: string | number
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        children,
        variant = 'primary',
        isDisabled = false,
        isLoading = false,
        isSkeleton = false,
        leftIcon,
        rightIcon,
        tooltip,
        tooltipPlacement = 'top',
        disabled = false,
        type = 'button',
        className,
        onClick,
        onClickInside,
        onClickOutside,
        onFocus,
        onBlur,
        onMouseEnter,
        onMouseLeave,
        onMouseOver,
        onMouseOut,
        onTouchStart,
        onTouchEnd,
        onTouchCancel,
        onTouchMove,
        ...attributes
    },
    forwardedRef
) {
    const boundary = useRef<HTMLSpanElement>(null)
    const disabledState = isDisabled || disabled
    const blocked = disabledState || isLoading || isSkeleton
    const hasText = hasContent(children)
    const hasTooltip = Boolean(tooltip?.trim())

    useEffect(() => {
        const element = boundary.current
        if (!element || !onClickOutside) return
        const document = element.ownerDocument
        const handleOutside = (event: MouseEvent) => {
            if (!event.composedPath().includes(element)) onClickOutside(event)
        }
        // Capture also observes outside clicks whose own handlers stop bubbling.
        document.addEventListener('click', handleOutside, true)
        return () => document.removeEventListener('click', handleOutside, true)
    }, [onClickOutside])

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (blocked) {
                event.preventDefault()
                return
            }
            onClickInside?.(event)
            onClick?.(event)
        },
        [blocked, onClickInside, onClick]
    )

    return (
        <Tooltip
            content={tooltip}
            placement={tooltipPlacement}
            disabled={!hasTooltip}
            onClick={(event) => {
                if (blocked) {
                    event.preventDefault()
                    event.stopPropagation()
                }
            }}
        >
            <span
                ref={boundary}
                className={`${baseClass}__trigger`}
                tabIndex={blocked && hasTooltip ? 0 : undefined}
                role={blocked && hasTooltip ? 'group' : undefined}
                aria-disabled={blocked && hasTooltip ? true : undefined}
                aria-label={blocked && hasTooltip ? (attributes['aria-label'] ?? tooltip) : undefined}
            >
                <button
                    {...attributes}
                    ref={forwardedRef}
                    type={type}
                    disabled={blocked}
                    aria-disabled={blocked || attributes['aria-disabled']}
                    aria-busy={isLoading || isSkeleton || attributes['aria-busy']}
                    className={[
                        baseClass,
                        `${baseClass}--${variant}`,
                        disabledState && `${baseClass}--disabled`,
                        isLoading && `${baseClass}--loading`,
                        isSkeleton && `${baseClass}--skeleton`,
                        blocked && hasTooltip && `${baseClass}--tooltipBlocked`,
                        className,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={handleClick}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseOver={onMouseOver}
                    onMouseOut={onMouseOut}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchCancel}
                    onTouchMove={onTouchMove}
                >
                    {hasContent(leftIcon) ? (
                        <span className={`${baseClass}__icon`} aria-hidden>
                            {leftIcon}
                        </span>
                    ) : null}
                    {hasText ? <span className={`${baseClass}__text`}>{children}</span> : null}
                    {hasContent(rightIcon) ? (
                        <span className={`${baseClass}__icon`} aria-hidden>
                            {rightIcon}
                        </span>
                    ) : null}
                    {isLoading && !isSkeleton ? <span className={`${baseClass}__spinner`} aria-hidden /> : null}
                    {isSkeleton ? (
                        <span className={`${baseClass}__skeleton`} aria-hidden>
                            <ApplySkeleton isLoading width="100%" height="100%" borderRadius="9999px" placeholder="" />
                        </span>
                    ) : null}
                </button>
            </span>
        </Tooltip>
    )
})

Button.displayName = 'ApplyDialogV2Button'

export default Button
